<script lang="ts">
  import { navigate } from '../lib/router'
  import { api, setToken, setUserType, setUserName } from '../lib/api'
  import { validateEmail } from '../lib/validator'
  import { onMount } from 'svelte'

  let email = ''
  let password = ''
  let error = ''
  let loading = false
  let emailError = ''

  async function login() {
    // Validar email antes de enviar
    emailError = ''
    if (!email || !email.trim()) {
      emailError = 'El email es requerido'
      return
    }
    if (!validateEmail(email)) {
      emailError = 'El email no tiene un formato válido'
      return
    }

    loading = true
    error = ''
    try {
      const res = await api.post('/api/v1/auth/login', { email, password })
      setToken(res.token)
      if (res.user_type_id) {
        setUserType(res.user_type_id)
      }
      if (res.name) {
        setUserName(res.name)
      } else if (res.email) {
        // Si no hay nombre, usar el email como nombre
        setUserName(res.email)
      }
      navigate('/sessions')
    } catch (e: any) {
      error = e.message
    } finally {
      loading = false
    }
  }
</script>

<div class="min-h-screen relative flex items-center justify-center overflow-hidden bg-gray-900">
  <!-- Animated Background Gradients -->
  <div class="absolute inset-0 w-full h-full">
    <div class="absolute top-0 -left-4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
    <div class="absolute top-0 -right-4 w-72 h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
    <div class="absolute -bottom-8 left-20 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
    <div class="absolute inset-0 bg-gradient-to-br from-indigo-900/50 via-purple-900/50 to-slate-900/80 backdrop-blur-3xl"></div>
  </div>

  <div class="relative w-full max-w-md p-6">
    <!-- Glass Card -->
    <div class="relative bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-2xl overflow-hidden">
      <!-- Decorative top line -->
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-pink-500 to-transparent opacity-50"></div>
      
      <div class="p-8 sm:p-10">
        <div class="text-center mb-10">
          <h1 class="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-300 via-purple-300 to-indigo-300 mb-2 tracking-tight">
            Bienvenido
          </h1>
          <p class="text-indigo-200/80 text-sm font-medium tracking-wide">GESTIÓN DE BOT WHATSAPP</p>
        </div>

        <form on:submit|preventDefault={login} class="space-y-6">
          <div class="space-y-2">
            <label class="text-xs font-bold text-indigo-200 uppercase tracking-wider ml-1">Email</label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-indigo-300 group-focus-within:text-pink-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.061.949" />
                </svg>
              </div>
              <input 
                type="email" 
                bind:value={email}
                class="w-full pl-11 pr-4 py-3.5 bg-black/20 border {emailError ? 'border-red-500/50' : 'border-white/10'} rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all duration-300 hover:bg-black/30"
                placeholder="nombre@empresa.com"
                required
                on:blur={() => {
                  if (email && !validateEmail(email)) {
                    emailError = 'El email no tiene un formato válido'
                  } else {
                    emailError = ''
                  }
                }}
              />
            </div>
            {#if emailError}
              <p class="text-red-300 text-xs mt-1 ml-1">{emailError}</p>
            {/if}
          </div>

          <div class="space-y-2">
            <label class="text-xs font-bold text-indigo-200 uppercase tracking-wider ml-1">Contraseña</label>
            <div class="relative group">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-indigo-300 group-focus-within:text-pink-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input 
                type="password" 
                bind:value={password}
                class="w-full pl-11 pr-4 py-3.5 bg-black/20 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all duration-300 hover:bg-black/30"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          {#if error}
            <div class="p-4 rounded-xl bg-red-500/10 border border-red-500/20 backdrop-blur-sm flex items-start space-x-3 animate-shake">
              <svg class="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p class="text-red-200 text-sm">{error}</p>
            </div>
          {/if}

          <button 
            type="submit" 
            disabled={loading}
            class="group w-full py-4 px-6 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-400 hover:via-purple-400 hover:to-indigo-400 text-white font-bold rounded-xl shadow-lg shadow-purple-500/30 transform transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden"
          >
            <div class="absolute inset-0 bg-white/20 group-hover:translate-x-full transition-transform duration-700 -skew-x-12 -translate-x-full"></div>
            {#if loading}
              <div class="flex items-center justify-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Iniciando...</span>
              </div>
            {:else}
              <span class="relative z-10">Ingresar al Sistema</span>
            {/if}
          </button>
        </form>
      </div>
      
      <!-- Footer decoration -->
      <div class="px-8 py-4 bg-black/20 border-t border-white/5 text-center">
        <p class="text-indigo-200/40 text-xs">© 2025 Monitor</p>
      </div>
    </div>
  </div>
</div>

<style>
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob {
    animation: blob 7s infinite;
  }
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  .animation-delay-4000 {
    animation-delay: 4s;
  }
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
    20%, 40%, 60%, 80% { transform: translateX(4px); }
  }
  .animate-shake {
    animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
  }
</style>
